import React from 'react';
import { Briefcase } from 'lucide-react';

const colorMap = {
  violet: { bg: 'bg-violet-500/20', text: 'text-violet-400', dot: 'bg-violet-500' },
  purple: { bg: 'bg-purple-500/20', text: 'text-purple-400', dot: 'bg-purple-500' },
  indigo: { bg: 'bg-indigo-500/20', text: 'text-indigo-400', dot: 'bg-indigo-500' },
  blue: { bg: 'bg-blue-500/20', text: 'text-blue-400', dot: 'bg-blue-500' },
};

const timeline = [
  {
    id: 1,
    company: 'LIDR.co | AI-Powered Careers',
    role: 'Mentor AI4Devs',
    period: 'Mar 2026 - Present',
    achievement: 'Guiding the next generation of developers in applied AI. Leading live sessions on end-to-end product development with AI (Copilot, Agents, LLMs) and providing 1:1 and group mentoring for Junior to Senior profiles.',
    color: 'violet',
  },
  {
    id: 2,
    company: 'Skydropx LATAM',
    role: 'Engineering Manager',
    period: 'Jan 2025 - Jan 2026',
    achievement: 'Led 4 cross-functional squads + QA area (~25 people). Implemented AI tools for auto-documentation and requirements refinement, cutting the Discovery cycle by 30% and aligning technical Roadmap with product OKRs.',
    color: 'purple',
  },
  {
    id: 3,
    company: 'PALO IT',
    role: 'Senior QA Automation Engineer',
    period: 'Sept 2022 - Dec 2023',
    achievement: 'Led BDD implementation enabling both technical and business profiles to contribute to coverage. Designed full-stack automation frameworks (Java, Selenium, Cypress) and built observability scripts with Grafana & K6 for high-demand environments.',
    color: 'indigo',
  },
  {
    id: 4,
    company: 'SQA - Software Quality Assurance',
    role: 'Senior QA Automation Engineer',
    period: 'Nov 2019 - Sept 2022',
    achievement: 'Trained 50+ professionals in QA automation with a 40% successful integration rate into real projects. Led multi-project teams across Web, Desktop, Mobile, and APIs — including banking projects with legacy architectures and strict compliance standards.',
    color: 'blue',
  },
];

const ProfessionalJourney = () => {
  return (
    <section id="journey" className="py-20 px-4 sm:px-6 lg:px-8 relative">
      <div className="max-w-6xl mx-auto">
        {/* Section Title */}
        <div className="text-center mb-16 fade-in-section">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="gradient-text">Professional Journey</span>
          </h2>
          <p className="text-gray-400 text-lg md:text-xl">
            Building excellence through innovation and leadership
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line — left on mobile, center on desktop */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-violet-500 via-purple-500 to-indigo-500 transform md:-translate-x-px"></div>

          {/* Timeline Items */}
          <div className="space-y-12">
            {timeline.map((item, index) => {
              const isEven = index % 2 === 0;
              return (
                <div
                  key={item.id}
                  className="relative fade-in-section"
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  {/* Card — mobile: right of left line; desktop: alternating sides */}
                  <div className={`ml-16 md:ml-0 md:flex ${isEven ? 'md:justify-end' : 'md:justify-start'}`}>
                    <div className={`glass glass-hover rounded-2xl p-6 md:p-8 md:w-5/12 ${isEven ? 'md:mr-12 md:text-right' : 'md:ml-12'}`}>
                      {/* Icon */}
                      <div className={`w-12 h-12 rounded-lg ${colorMap[item.color].bg} flex items-center justify-center mb-4 ${isEven ? 'md:ml-auto' : ''}`}>
                        <Briefcase className={`w-6 h-6 ${colorMap[item.color].text}`} />
                      </div>

                      {/* Company */}
                      <h3 className="text-2xl font-bold text-white mb-1">
                        {item.company}
                      </h3>

                      {/* Role */}
                      <p className={`${colorMap[item.color].text} font-medium mb-2`}>
                        {item.role}
                      </p>

                      {/* Period */}
                      <p className="text-gray-500 text-sm mb-4">
                        {item.period}
                      </p>

                      {/* Achievement */}
                      <p className="text-gray-400 leading-relaxed">
                        {item.achievement}
                      </p>
                    </div>
                  </div>

                  {/* Timeline Dot */}
                  <div className="absolute left-8 md:left-1/2 top-6 transform -translate-x-1/2">
                    <div className={`w-4 h-4 rounded-full ${colorMap[item.color].dot} ring-4 ring-github-dark animate-pulse`}></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProfessionalJourney;
